import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import api from '../services/api';

interface QuestionScore {
  questionIndex: number;
  question: string;
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  points: number;
  maxPoints: number;
  explanation?: string;
}

interface EvaluationDetails {
  evaluationId: string;
  title: string;
  description: string;
  totalAttempts: number;
  bestScore: number;
  earnedPoints: number;
  totalPoints: number;
  passed: boolean;
  completedAt: string;
  questionScores: QuestionScore[];
}

interface LessonDetail {
  lessonId: string;
  title: string;
  description: string;
  order: number;
  hasEvaluation: boolean;
  evaluationDetails?: EvaluationDetails;
}

interface CourseDetail {
  courseId: string;
  title: string;
  description: string;
  status: string;
  completionPercentage: number;
  completedLessons: number;
  totalLessons: number;
  finalScore: number;
  enrolledAt: string;
  lastActivity: string;
  lessonDetails: LessonDetail[];
}

interface UserCourseDetailDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  userEmail: string;
}

const UserCourseDetailDialog: React.FC<UserCourseDetailDialogProps> = ({
  open,
  onClose,
  userId,
  userName,
  userEmail
}) => {
  const [courseDetails, setCourseDetails] = useState<CourseDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && userId) {
      loadUserCourseDetails();
    }
  }, [open, userId]);

  const loadUserCourseDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getUserCourseDetails(userId);
      setCourseDetails(data.courseDetails || []);
    } catch (err: any) {
      console.error('Error cargando detalles de cursos:', err);
      setError(err.response?.data?.message || 'Error cargando detalles de cursos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'not_started': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Completado';
      case 'in_progress': return 'En Progreso';
      case 'not_started': return 'No Iniciado';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SchoolIcon color="primary" />
          <Box>
            <Typography variant="h6" component="div">
              Detalles de Cursos - {userName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userEmail}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && courseDetails.length === 0 && (
          <Alert severity="info">
            Este usuario no tiene cursos registrados.
          </Alert>
        )}

        {!loading && !error && courseDetails.map((course, courseIndex) => (
          <Card key={course.courseId} sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {course.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {course.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Chip 
                      label={getStatusText(course.status)} 
                      color={getStatusColor(course.status) as any}
                      size="small"
                    />
                    <Chip 
                      icon={<TrendingUpIcon />}
                      label={`${course.completionPercentage}% Completado`}
                      color="info"
                      size="small"
                    />
                    <Chip 
                      icon={<SchoolIcon />}
                      label={`${course.completedLessons}/${course.totalLessons} Lecciones`}
                      color="primary"
                      size="small"
                    />
                    {course.finalScore !== null && course.finalScore !== undefined && (
                      <Chip 
                        label={`${course.finalScore} pts`}
                        color="secondary"
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Inscrito: {formatDate(course.enrolledAt)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Última actividad: {formatDate(course.lastActivity)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SchoolIcon fontSize="small" />
                Lecciones del Curso
              </Typography>

              {course.lessonDetails.map((lesson, lessonIndex) => (
                <Accordion key={lesson.lessonId} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {lesson.order}. {lesson.title}
                      </Typography>
                      {lesson.hasEvaluation && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {lesson.evaluationDetails ? (
                            <>
                              <Chip 
                                icon={lesson.evaluationDetails.passed ? <CheckCircleIcon /> : <CancelIcon />}
                                label={lesson.evaluationDetails.passed ? 'Aprobado' : 'Reprobado'}
                                color={lesson.evaluationDetails.passed ? 'success' : 'error'}
                                size="small"
                              />
                              <Chip 
                                label={`${lesson.evaluationDetails.earnedPoints}/${lesson.evaluationDetails.totalPoints} pts`}
                                color={getScoreColor(lesson.evaluationDetails.earnedPoints, lesson.evaluationDetails.totalPoints) as any}
                                size="small"
                              />
                            </>
                          ) : (
                            <Chip 
                              icon={<QuizIcon />}
                              label="Evaluación Pendiente"
                              color="warning"
                              size="small"
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </AccordionSummary>
                  
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {lesson.description}
                    </Typography>

                    {lesson.hasEvaluation && lesson.evaluationDetails && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QuizIcon fontSize="small" />
                          Detalles de la Evaluación
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Título:</strong> {lesson.evaluationDetails.title}
                          </Typography>
                          {lesson.evaluationDetails.description && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Descripción:</strong> {lesson.evaluationDetails.description}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            <strong>Intentos:</strong> {lesson.evaluationDetails.totalAttempts}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Completado:</strong> {formatDate(lesson.evaluationDetails.completedAt)}
                          </Typography>
                        </Box>

                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Pregunta</strong></TableCell>
                                <TableCell><strong>Respuesta del Usuario</strong></TableCell>
                                <TableCell><strong>Respuesta Correcta</strong></TableCell>
                                <TableCell align="center"><strong>Resultado</strong></TableCell>
                                <TableCell align="center"><strong>Puntos</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lesson.evaluationDetails.questionScores.map((question, qIndex) => (
                                <TableRow key={qIndex}>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {question.questionIndex}. {question.question}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography 
                                      variant="body2" 
                                      color={question.isCorrect ? 'success.main' : 'error.main'}
                                    >
                                      {question.userAnswer}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="success.main">
                                      {question.correctAnswer}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    {question.isCorrect ? (
                                      <CheckCircleIcon color="success" fontSize="small" />
                                    ) : (
                                      <CancelIcon color="error" fontSize="small" />
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="bold"
                                      color={question.isCorrect ? 'success.main' : 'error.main'}
                                    >
                                      {question.points}/{question.maxPoints}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {lesson.evaluationDetails.questionScores.some(q => q.explanation) && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Explicaciones:
                            </Typography>
                            {lesson.evaluationDetails.questionScores
                              .filter(q => q.explanation)
                              .map((question, qIndex) => (
                                <Box key={qIndex} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Pregunta {question.questionIndex}:</strong> {question.explanation}
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        )}
                      </Box>
                    )}

                    {lesson.hasEvaluation && !lesson.evaluationDetails && (
                      <Alert severity="info">
                        Esta lección tiene una evaluación, pero el usuario aún no la ha completado.
                      </Alert>
                    )}

                    {!lesson.hasEvaluation && (
                      <Alert severity="info">
                        Esta lección no tiene evaluación asociada.
                      </Alert>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserCourseDetailDialog;
